import { BrandMark } from "../brand-mark";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { ArrowUpRight, Check, Copy, Menu, X } from "lucide-react";
import { ThemeSwitcher } from "../theme-switcher";
import { APP_VERSION } from "../../lib/version";

export function Brand() {
  return (
    <a className="mk-brand" href="/" aria-label="Proximaforma home">
      <BrandMark className="brand-symbol" />
      <span className="brand-wordmark">proxima forma</span>
    </a>
  );
}

export function Action({
  href = "/app?auth=signup",
  children = "Create an account",
  secondary = false,
}: {
  href?: string;
  children?: ReactNode;
  secondary?: boolean;
}) {
  return (
    <a
      className={`mk-action ${secondary ? "mk-action-secondary" : ""}`}
      href={href}
    >
      {children}
      <ArrowUpRight size={17} aria-hidden="true" />
    </a>
  );
}

export function Layout({
  path,
  children,
}: {
  path: string;
  children: ReactNode;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const redesign =
    path === "/" ||
    path === "/community" ||
    path === "/pricing" ||
    path === "/impressum" ||
    path === "/integrations" ||
    path.startsWith("/blog") ||
    path.startsWith("/connect/");
  const menuButton = useRef<HTMLButtonElement>(null);
  const navigation = useRef<HTMLElement>(null);
  useEffect(() => {
    if (!menuOpen) return;
    navigation.current?.querySelector("a")?.focus();
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
        menuButton.current?.focus();
      }
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [menuOpen]);
  const links = [
    ["/product", "Product"],
    ["/integrations", "Connect your agent"],
    ["/pricing", "Pricing"],
    ["/community", "Community"],
    ["/blog", "Blog"],
  ];
  return (
    <div className={`marketing${path === "/" ? " mk-home-layout" : ""}`}>
      <a className="mk-skip" href="#main-content">
        Skip to content
      </a>
      <header className="mk-header">
        <div className="mk-nav-wrap">
          <Brand />
          <nav
            ref={navigation}
            className={`mk-nav ${menuOpen ? "is-open" : ""}`}
            id="marketing-navigation"
            aria-label="Main navigation"
          >
            {links.map(([href, label]) => (
              <a
                key={href}
                href={href}
                onClick={() => setMenuOpen(false)}
                aria-current={
                  path === href ||
                  path.startsWith(href + "/") ||
                  (href === "/integrations" && path.startsWith("/connect/"))
                    ? "page"
                    : undefined
                }
              >
                {label}
              </a>
            ))}
          </nav>
          <div className="mk-nav-actions">
            <a href="/app" className="mk-text-link">
              Sign in
            </a>
            <Action>Start creating</Action>
            <button
              ref={menuButton}
              className="mk-menu"
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              aria-expanded={menuOpen}
              aria-controls="marketing-navigation"
              onClick={() => setMenuOpen(!menuOpen)}
              onKeyDown={(event) => {
                if (event.key === "Escape") setMenuOpen(false);
              }}
            >
              {menuOpen ? <X size={21} /> : <Menu size={21} />}
            </button>
          </div>
        </div>
      </header>
      <main id="main-content" tabIndex={-1}>
        {children}
      </main>
      {redesign ? (
        <footer className="mk-redesign-footer mk-home-container">
          <div className="mk-redesign-footer-brand"><a href="/" aria-label="Proxima Forma home">proxima forma</a><p>Design infrastructure for<br />people and agents.</p><small>© 2026 Proxima Forma</small></div>
          <nav aria-label="Product"><strong>Product</strong><a aria-label="Product overview" href="/product">Product</a><a aria-label="Pricing details" href="/pricing">Pricing</a><a aria-label="Product changelog" href="/changelog">Changelog</a></nav>
          <nav aria-label="Connect"><strong>Connect</strong><a aria-label="Connect ChatGPT" href="/connect/chatgpt">ChatGPT</a><a aria-label="Connect Claude" href="/connect/claude">Claude</a><a aria-label="Custom agent connectors" href="/connect/agent">Custom connectors</a></nav>
          <nav aria-label="Explore"><strong>Explore</strong><a aria-label="Community showcase" href="/community">Community</a><a aria-label="Blog articles" href="/blog">Blog</a><a aria-label="Documentation" href="/docs">Docs</a></nav>
          <nav aria-label="Legal"><strong>Legal</strong><a aria-label="Privacy policy" href="/privacy">Privacy</a><a aria-label="Terms of service" href="/terms">Terms</a><a aria-label="Legal notice" href="/impressum">Impressum</a></nav>
        </footer>
      ) : (
        <footer className="mk-footer">
          <div className="mk-footer-top mk-container">
            <div>
              <Brand />
              <p>
                A little human direction.
                <br />A whole new world of form.
              </p>
              <span className="mk-footer-version">
                <span /> Product preview · v{APP_VERSION}
              </span>
            </div>
            <div>
              <h2>Make something</h2>
              <a href="/product">The workspace</a>
              <a href="/use-cases">Ways to work</a>
              <a href="/integrations">Agent connections</a>
              <a href="/pricing">Pricing</a>
            </div>
            <div>
              <h2>Find your footing</h2>
              <a href="/docs">Getting started</a>
              <a href="/docs/workspace">Canvas guide</a>
              <a href="/docs/mcp">MCP documentation</a>
              <a href="/docs/api">API reference</a>
            </div>
            <div>
              <h2>Keep in the loop</h2>
              <a href="/changelog">What’s new</a>
              <a href="/privacy">Your data</a>
              <a href="/terms">Terms of service</a>
              <a href="/support">Contact support</a>
              <a href="/app">Open the app ↗</a>
            </div>
          </div>
          <div className="mk-footer-bottom mk-container">
            <span>© {2026} Proximaforma</span>
            <span>Designed for people. Open to agents.</span>
            <ThemeSwitcher />
            <a href="#main-content">Back to top ↑</a>
          </div>
        </footer>
      )}
    </div>
  );
}

export function PageIntro({
  eyebrow,
  title,
  children,
}: {
  eyebrow: string;
  title: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="mk-page-intro mk-container">
      <span className="mk-eyebrow">{eyebrow}</span>
      <h1>{title}</h1>
      <p>{children}</p>
    </div>
  );
}

export function ClosingCTA() {
  return (
    <section className="mk-closing mk-container">
      <div>
        <span className="mk-eyebrow">The next idea is yours</span>
        <h2>
          Let’s give it
          <br />
          some form.
        </h2>
      </div>
      <div>
        <p>
          Open a canvas. Bring an agent.
          <br />
          Make something worth refining.
        </p>
        <Action />
        <span className="mk-fine">Private canvases. Bring your own agent.</span>
      </div>
    </section>
  );
}

export function CodeBlock({
  value,
  label = "Example",
}: {
  value: string;
  label?: string;
}) {
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">(
    "idle",
  );
  useEffect(() => setCopyState("idle"), [value]);
  return (
    <div className="mk-code">
      <div>
        <span>{label}</span>
        <button
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(value);
              setCopyState("copied");
            } catch {
              setCopyState("failed");
            }
          }}
          aria-label={`Copy ${label}`}
        >
          {copyState === "copied" ? <Check size={14} /> : <Copy size={14} />}
          <span aria-live="polite">
            {copyState === "copied"
              ? "Copied"
              : copyState === "failed"
                ? "Select text to copy"
                : "Copy"}
          </span>
        </button>
      </div>
      <pre tabIndex={0}>
        <code>{value}</code>
      </pre>
    </div>
  );
}

export const faqItems = [
  [
    "What can I design?",
    "Product screens, mobile interfaces, dashboards, and early application concepts. Start with frames, text, shapes, vector paths, buttons, and images, then refine their position and appearance on the canvas.",
  ],
  [
    "Can I bring my own AI agent?",
    "Yes. Connect your own agent through MCP or the REST API to read and edit your canvas. Your agent’s provider account and usage charges are separate.",
  ],
  [
    "Can I edit what an agent creates?",
    "Leave a pinned comment to guide your agent. It can read the thread, refine the editable design, reply, and mark feedback handled. The advanced editor remains available for manual changes.",
  ],
  [
    "Where does my work live?",
    "In the database on the computer running this installation. Accounts have private canvases, with access controlled for reviewers and agents. SVG and JSON exports let you take your designs with you.",
  ],
  [
    "Does it generate a working application?",
    "The current version creates visual interface designs. It does not yet generate application code, interactive prototypes, auto layout, or reusable component instances.",
  ],
  [
    "Can my team work in it remotely?",
    "Accounts and invitations support shared review with view-only or commenting access. This installation runs locally; remote teammates need a deployed HTTPS instance. A managed hosting service is not included.",
  ],
];

export function FAQ() {
  return (
    <section className="mk-faq mk-container">
      <div>
        <span className="mk-eyebrow">A few good questions</span>
        <h2>
          A little clarity
          <br />
          before you create.
        </h2>
        <a className="mk-text-link" href="/docs">
          Find your way around <ArrowUpRight size={16} />
        </a>
      </div>
      <div>
        {faqItems.map(([question, answer]) => (
          <details key={question}>
            <summary>
              {question}
              <span aria-hidden="true">+</span>
            </summary>
            <p>{answer}</p>
          </details>
        ))}
      </div>
    </section>
  );
}
